<template>
	<div class="container px-5">
		<p class="small text-muted mb-3">
			<strong>Standing order (product order):</strong> a scheduled transfer.
			<span class="d-block mt-1">
				<strong>Ordering customer</strong> is the member whose account is debited.
				The <strong>receiving account</strong> may belong to any member.
			</span>
			<span v-if="branchId != null" class="d-block mt-1"
				>Branch: <strong>{{ branchId }}</strong> (sent as <code>branchId</code>).</span
			>
		</p>
		<div class="row">
			<div class="col-12 mb-3">
				<label class="akkurate-dark small fw-bold" for="ordering_customer_code"
					>Ordering customer (payer)</label
				>
				<b-form-input
					id="ordering_customer_code"
					class="field-container fields mt-1"
					type="text"
					placeholder="Customer code"
					v-model.trim="form.ordering_customer_code"
					autocomplete="off"
				/>
				<small class="text-muted d-block mt-1"
					>Funds are taken from this customer’s source account only.</small
				>
			</div>

			<div class="col-md-6">
				<div class="col-12 text-center mb-3">
					<h6 class="akkurate-dark fw-bold">Source account (debited)</h6>
				</div>
				<div class="col-12">
					<div class="row mb-2">
						<div class="col-12">
							<b-button
								type="button"
								size="sm"
								class="akkurate-green-btn mb-2"
								:disabled="sourceSearchBusy || !hasOrderingCode"
								@click="loadSourceAccounts"
							>
								<font-awesome-icon class="small me-1" :icon="['fas', 'search']" />
								Load this customer’s accounts
							</b-button>
						</div>
					</div>
					<div class="row mb-3">
						<div class="col-12">
							<label class="akkurate-dark small" for="source_pick">Account</label>
							<multiselect
								id="source_pick"
								class="field-container"
								v-model="form.source_account"
								:options="sourceAccountOptions"
								:custom-label="accountOptionLabel"
								placeholder="Select source account"
								selectLabel=""
								deselectLabel=""
								track-by="account_number"
								@input="onSourceAccountChange"
							/>
							<small class="text-muted d-block mt-1"
								>Must belong to the ordering customer above.</small
							>
						</div>
					</div>

					<div class="row mb-3">
						<div class="col-12">
							<label class="akkurate-dark small" for="source_name">Account holder</label>
							<b-form-input
								id="source_name"
								class="field-container fields"
								type="text"
								:value="sourceFullName"
								readonly
								disabled
							/>
						</div>
					</div>

					<div class="row mb-3">
						<div class="col-12">
							<label class="akkurate-dark small" for="amount">Amount</label>
							<b-form-input
								id="amount"
								class="field-container fields"
								type="text"
								placeholder="Amount"
								v-model="form.amount"
								autocomplete="off"
							/>
						</div>
					</div>

					<div class="row mb-3">
						<div class="col-12">
							<label class="akkurate-dark small" for="circle">Schedule (circle)</label>
							<multiselect
								id="circle"
								class="field-container"
								v-model="form.circle"
								:options="circle_options"
								label="label"
								track-by="value"
								placeholder="Select frequency"
								selectLabel=""
								deselectLabel=""
							/>
							<small class="text-muted d-block mt-1"
								>daily, weekly, monthly, or yearly (sent as annually).</small
							>
						</div>
					</div>
				</div>
			</div>

			<div class="col-md-6">
				<div class="col-12 text-center mb-3">
					<h6 class="akkurate-dark fw-bold">Receiving account (credited)</h6>
				</div>
				<div class="col-12">
					<div class="row mb-2">
						<div class="col-12">
							<label class="akkurate-dark small" for="dest_query">Find account</label>
							<b-form-input
								id="dest_query"
								class="field-container fields mb-2"
								type="text"
								placeholder="Customer code, mobile, name, or account #"
								v-model.trim="destinationSearchQuery"
								autocomplete="off"
							/>
							<b-button
								type="button"
								size="sm"
								class="akkurate-green-btn mb-2"
								:disabled="destinationSearchBusy || !destinationSearchQuery"
								@click="loadDestinationAccounts"
							>
								<font-awesome-icon class="small me-1" :icon="['fas', 'search']" />
								Search accounts
							</b-button>
							<small class="text-muted d-block mb-2"
								>Any valid member account — not limited to the ordering customer.</small
							>
						</div>
					</div>

					<div class="row mb-3">
						<div class="col-12">
							<label class="akkurate-dark small" for="dest_pick">Receiving account</label>
							<multiselect
								id="dest_pick"
								class="field-container"
								v-model="form.destination_account"
								:options="destinationAccountOptions"
								:custom-label="accountOptionLabel"
								placeholder="Select destination account"
								selectLabel=""
								deselectLabel=""
								track-by="account_number"
								@input="onDestinationAccountChange"
							/>
						</div>
					</div>

					<div class="row mb-3">
						<div class="col-12">
							<label class="akkurate-dark small" for="dest_name">Account holder</label>
							<b-form-input
								id="dest_name"
								class="field-container fields"
								type="text"
								:value="destinationFullName"
								readonly
								disabled
							/>
						</div>
					</div>

					<div class="row mb-3">
						<div class="col-12">
							<label class="akkurate-dark small" for="start_date">Start date</label>
							<b-form-input
								id="start_date"
								class="field-container fields"
								type="date"
								v-model="form.start_date"
							/>
						</div>
					</div>

					<div class="row mb-3">
						<div class="col-12">
							<label class="akkurate-dark small" for="end_date">End date</label>
							<b-form-input
								id="end_date"
								class="field-container fields"
								type="date"
								v-model="form.end_date"
							/>
							<small class="text-muted d-block mt-1">Must be on or after the start date.</small>
						</div>
					</div>
				</div>
			</div>

			<div class="mt-auto col-12 text-end">
				<b-button
					type="button"
					class="akkurate-green-btn mb-3 btn-size"
					:disabled="submitBusy"
					@click.prevent="$emit('submit-order')"
				>
					<font-awesome-icon
						class="small"
						:icon="['fas', editingOrderId != null ? 'save' : 'plus']"
					/>
					{{ submitLabel }}
				</b-button>
			</div>
		</div>
	</div>
</template>
<script>
import Multiselect from "vue-multiselect";
import ApiService from "@/core/services/api.service";
import {
	STANDING_ORDER_CIRCLE_OPTIONS,
	extractCoaSubheadIdFromAccount,
} from "@/utils/productOrderValidation.js";

export default {
	props: {
		form: {
			type: Object,
			required: true,
		},
		submitBusy: {
			type: Boolean,
			default: false,
		},
		/** Resolved from users/profile; sent as branchId */
		branchId: {
			default: null,
		},
		/** Product order id when editing (from search); null for create */
		editingOrderId: {
			default: null,
		},
	},
	components: {
		Multiselect,
	},
	data() {
		return {
			circle_options: STANDING_ORDER_CIRCLE_OPTIONS,
			sourceAccountOptions: [],
			destinationAccountOptions: [],
			destinationSearchQuery: "",
			sourceSearchBusy: false,
			destinationSearchBusy: false,
		};
	},
	computed: {
		hasOrderingCode() {
			return (
				String(this.form.ordering_customer_code || "").trim().length > 0
			);
		},
		sourceFullName() {
			return this.customerDisplayName(this.form.source_account);
		},
		destinationFullName() {
			return this.customerDisplayName(this.form.destination_account);
		},
		submitLabel() {
			return this.editingOrderId != null ? "Update order" : "Add order";
		},
	},
	methods: {
		customerDisplayName(accountRow) {
			if (!accountRow || !accountRow.customer) {
				return "";
			}
			const c = accountRow.customer;
			const parts = [
				c.first_name,
				c.middlename || c.middle_name,
				c.last_name,
			].filter(Boolean);
			return parts.join(" ").trim();
		},
		accountOptionLabel(row) {
			if (!row) {
				return "";
			}
			const num = row.account_number || "";
			const prod = row.account_type && row.account_type.name ? row.account_type.name : "";
			const name = this.customerDisplayName(row);
			return [num, prod, name].filter(Boolean).join(" — ");
		},
		onSourceAccountChange(acc) {
			const id = extractCoaSubheadIdFromAccount(acc);
			this.$set(this.form, "source_coa_id", id);
		},
		onDestinationAccountChange(acc) {
			const id = extractCoaSubheadIdFromAccount(acc);
			this.$set(this.form, "destination_coa_id", id);
		},
		async loadSourceAccounts() {
			const code = String(this.form.ordering_customer_code || "").trim();
			if (!code) {
				return;
			}
			this.sourceSearchBusy = true;
			try {
				const response = await ApiService.post("/accounts/paginate", {
					page: 1,
					per_page: 50,
					filter: code,
					branch_id: "",
					account_type_id: "",
					account_number: "",
					phone_number: "",
					start_date: "",
					end_date: "",
					status: "",
				});
				this.sourceAccountOptions = response.data.data || [];
				if (this.sourceAccountOptions.length === 0) {
					this.$bvToast.toast("No accounts found for this customer code.", {
						title: "Standing order",
						variant: "warning",
						solid: true,
						autoHideDelay: 4000,
					});
				}
			} catch (e) {
				this.sourceAccountOptions = [];
			} finally {
				this.sourceSearchBusy = false;
			}
		},
		async loadDestinationAccounts() {
			const q = String(this.destinationSearchQuery || "").trim();
			if (!q) {
				return;
			}
			this.destinationSearchBusy = true;
			try {
				const response = await ApiService.post("/accounts/paginate", {
					page: 1,
					per_page: 50,
					filter: q,
					branch_id: "",
					account_type_id: "",
					account_number: "",
					phone_number: "",
					start_date: "",
					end_date: "",
					status: "",
				});
				this.destinationAccountOptions = response.data.data || [];
				if (this.destinationAccountOptions.length === 0) {
					this.$bvToast.toast("No accounts matched your search.", {
						title: "Standing order",
						variant: "warning",
						solid: true,
						autoHideDelay: 4000,
					});
				}
			} catch (e) {
				this.destinationAccountOptions = [];
			} finally {
				this.destinationSearchBusy = false;
			}
		},
	},
};
</script>
